/**
 * ReportTable Component
 *
 * A flexible table component specifically designed for displaying dynamic report data.
 * Automatically generates columns based on data structure and handles various data types.
 */

import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Table, Button, Card, Spinner, Modal } from "../shared";
import {
  generateColumnsFromData,
  exportToCsv,
  generateReportSummary,
} from "../utils/reportUtils";
import ReportTableCell from "./ReportTableCell";
import { useSaveReportMutation } from "../redux/services/reports/reportsApi";
import { toast } from "react-toastify";
import { downloadReportPDF } from "../utils/reportPdfGenerator.jsx";
import { getTableConfig } from "../layouts/ReportLayout/table.configs/show";
import { formatDate, renderAmount } from "../utils";
import {
  formatOperationalWindowEndForDisplay,
  formatOperationalWindowStartForDisplay,
} from "../utils/date.utils";
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
  EyeIcon,
  EyeSlashIcon,
  BookmarkIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";

/**
 * ReportTable - Flexible table for report data
 *
 * @param {Object} props
 * @param {Array} props.data - Report data array
 * @param {string} props.reportName - Name of the report
 * @param {Object} props.reportConfig - Report configuration object
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onRefresh - Callback for refreshing data
 * @param {Object} props.columnOptions - Options for column generation
 * @param {boolean} props.showSummary - Whether to show summary statistics
 * @param {boolean} props.allowExport - Whether to allow CSV export
 * @param {boolean} props.allowSave - Whether to allow saving reports
 * @param {Object} props.reportMetadata - Additional metadata for saving
 */
const ReportTable = ({
  data = [],
  reportName = "Report",
  reportConfig,
  loading = false,
  onRefresh,
  columnOptions = {},
  showSummary = true,
  allowExport = true,
  allowSave = true,
  reportMetadata = {},
  parentCompany = {},
  reportRequest = null, // Query parameters for regenerating the report
  analytics = null, // Invoice analytics block from the reports API, drawn into the PDF
  ...tableProps
}) => {
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [saveReport, { isLoading: isSaving }] = useSaveReportMutation();
  const [selectedField, setSelectedField] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const navigate = useNavigate();

  // Detect if there are multiple employees/drivers in the data
  const hasMultipleEmployees = useMemo(() => {
    if (!data || data.length === 0) return false;

    const modelName = reportConfig?.modelName || "";
    const employeeField = modelName === "Payout" ? "employee" : "driver";
    const driverField = "driver";

    // Get unique employee/driver IDs
    const employeeIds = new Set();
    data.forEach((item) => {
      if (item[employeeField]?._id) {
        employeeIds.add(item[employeeField]._id.toString());
      } else if (item[employeeField]) {
        employeeIds.add(item[employeeField].toString());
      }
      // Also check driver field for Payouts
      if (modelName === "Payout" && item[driverField]?._id) {
        employeeIds.add(item[driverField]._id.toString());
      } else if (modelName === "Payout" && item[driverField]) {
        employeeIds.add(item[driverField].toString());
      }
    });

    return employeeIds.size > 1;
  }, [data, reportConfig]);

  // Detect if an invoice report includes more than one client
  const hasMultipleClients = useMemo(() => {
    if (!data || data.length === 0) return false;

    const modelName = reportConfig?.modelName || "";
    if (modelName !== "Invoice" && modelName !== "Invoices") return false;

    const clientIds = new Set();
    data.forEach((item) => {
      const client = item.client;
      if (client?._id) {
        clientIds.add(String(client._id));
      } else if (client && typeof client !== "object") {
        clientIds.add(String(client));
      }
    });

    return clientIds.size > 1;
  }, [data, reportConfig]);

  // Transform grouped data into parent-child structure
  const groupedData = useMemo(() => {
    if (!data || data.length === 0) return data;

    const modelName = reportConfig?.modelName || "";

    // Check if data is grouped by payDate (has _groupPayDate field)
    // ModelName can be "Payout" or "Payouts"
    const isGroupedByPayDate =
      (modelName === "Payout" || modelName === "Payouts") &&
      data.length > 0 &&
      data.some((item) => item._groupPayDate);

    // Debug logging
    if (process.env.NODE_ENV === "development") {
      console.log("ReportTable groupedData check:", {
        modelName,
        dataLength: data.length,
        hasGroupPayDate: data.some((item) => item._groupPayDate),
        isGroupedByPayDate,
        firstItem: data[0] ? Object.keys(data[0]) : null,
        firstItemGroupPayDate: data[0]?._groupPayDate,
        firstItemGroupPayDateString: data[0]?._groupPayDateString,
      });
    }

    // If not grouped, return data as-is
    if (!isGroupedByPayDate) return data;

    // Group consecutive payouts by _groupPayDate
    const groups = new Map();
    const groupOrder = [];

    data.forEach((payout) => {
      const groupKey =
        payout._groupPayDateString ||
        (payout._groupPayDate instanceof Date
          ? format(payout._groupPayDate, "yyyy-MM-dd")
          : format(new Date(payout._groupPayDate), "yyyy-MM-dd"));

      if (!groups.has(groupKey)) {
        // Create parent row for this group
        const parentRow = {
          _id: `group-${groupKey}`,
          _isGroupRow: true,
          _groupPayDate: payout._groupPayDate,
          _groupPayDateString: groupKey,
          _groupTotalPayouts: payout._groupTotalPayouts || 0,
          _groupTotalAmount: payout._groupTotalAmount || 0,
          _groupTotalMileage: payout._groupTotalMileage || 0,
          _groupTotalHours: payout._groupTotalHours || 0,
          _groupTotalMileageTotal: payout._groupTotalMileageTotal || 0,
          _groupTotalHourlyTotal: payout._groupTotalHourlyTotal || 0,
          _groupTotalSupplementalTotal:
            payout._groupTotalSupplementalTotal || 0,
          _groupManifestStartDate: payout._groupManifestStartDate,
          _groupManifestEndDate: payout._groupManifestEndDate,
          payDate: payout._groupPayDate, // For display
          subRows: [],
        };
        groups.set(groupKey, parentRow);
        groupOrder.push(groupKey);
      }

      // Add payout as child row
      groups.get(groupKey).subRows.push(payout);
    });

    // Return array of parent rows with subRows
    const result = groupOrder.map((key) => groups.get(key));

    // Debug logging
    if (process.env.NODE_ENV === "development") {
      console.log("ReportTable groupedData result:", {
        groupCount: result.length,
        groups: result.map((g) => ({
          id: g._id,
          payDate: g._groupPayDate,
          payDateString: g._groupPayDateString,
          subRowsCount: g.subRows?.length || 0,
          firstSubRowId: g.subRows?.[0]?._id,
        })),
      });
    }

    return result;
  }, [data, reportConfig]);

  // Sort data by employee/driver first, then by date (for multiple employees)
  // Also handle grouping by payDate when enabled
  const sortedData = useMemo(() => {
    // Use groupedData if available, otherwise use original data
    const dataToSort =
      groupedData && groupedData.some((item) => item._isGroupRow)
        ? groupedData
        : data;

    if (!dataToSort || dataToSort.length === 0) return dataToSort;

    const modelName = reportConfig?.modelName || "";
    const dateField = modelName === "Payout" ? "payDate" : "dateCreated";
    const employeeField = modelName === "Payout" ? "employee" : "driver";
    const driverField = "driver";

    // Check if data is grouped (has _isGroupRow)
    const isGrouped = dataToSort.some((item) => item._isGroupRow);

    if (isGrouped) {
      // Sort parent rows by group payDate (newest first)
      return [...dataToSort].sort((a, b) => {
        const getGroupDate = (item) => {
          if (item._groupPayDate) {
            return item._groupPayDate instanceof Date
              ? item._groupPayDate
              : new Date(item._groupPayDate);
          }
          if (item._groupPayDateString) {
            return new Date(item._groupPayDateString);
          }
          return new Date(0);
        };

        const aGroupDate = getGroupDate(a);
        const bGroupDate = getGroupDate(b);

        if (aGroupDate.getTime() !== bGroupDate.getTime()) {
          return bGroupDate - aGroupDate; // Newest first
        }

        // Sort child rows within each group by individual payDate
        if (a.subRows && b.subRows) {
          a.subRows.sort((x, y) => {
            const xDate = x[dateField] ? new Date(x[dateField]) : new Date(0);
            const yDate = y[dateField] ? new Date(y[dateField]) : new Date(0);
            return yDate - xDate; // Newest first
          });
        }

        return 0;
      });
    }

    // If not grouped and no multiple employees, return as-is
    if (!hasMultipleEmployees) return dataToSort;

    // Get employee/driver ID for comparison
    const getEmployeeId = (item) => {
      if (modelName === "Payout") {
        return (
          item[employeeField]?._id?.toString() ||
          item[employeeField]?.toString() ||
          item[driverField]?._id?.toString() ||
          item[driverField]?.toString() ||
          ""
        );
      }
      return (
        item[employeeField]?._id?.toString() ||
        item[employeeField]?.toString() ||
        ""
      );
    };

    return [...dataToSort].sort((a, b) => {
      const aEmployeeId = getEmployeeId(a);
      const bEmployeeId = getEmployeeId(b);

      // First sort by employee/driver
      if (aEmployeeId !== bEmployeeId) {
        return aEmployeeId.localeCompare(bEmployeeId);
      }

      // Then sort by date within each employee group
      const aDate = a[dateField] ? new Date(a[dateField]) : new Date(0);
      const bDate = b[dateField] ? new Date(b[dateField]) : new Date(0);
      return bDate - aDate; // Newest first
    });
  }, [groupedData, data, hasMultipleEmployees, reportConfig]);

  // Check if data is grouped
  const isGrouped = useMemo(() => {
    const grouped =
      sortedData &&
      sortedData.length > 0 &&
      sortedData.some((item) => item._isGroupRow);

    // Debug logging
    if (process.env.NODE_ENV === "development") {
      console.log("ReportTable isGrouped check:", {
        sortedDataLength: sortedData?.length || 0,
        hasGroupRow: sortedData?.some((item) => item._isGroupRow) || false,
        isGrouped: grouped,
        firstItem: sortedData?.[0]
          ? {
              _id: sortedData[0]._id,
              _isGroupRow: sortedData[0]._isGroupRow,
              hasSubRows: !!sortedData[0].subRows,
              subRowsCount: sortedData[0].subRows?.length || 0,
            }
          : null,
      });
    }

    return grouped;
  }, [sortedData]);

  // Generate columns for nested table (individual payouts)
  const nestedTableColumns = useMemo(() => {
    const modelName = reportConfig?.modelName || "";
    if (modelName !== "Payout" && modelName !== "Payouts") return [];

    const { tableConfig } = getTableConfig(modelName);
    const columnDefinitions = tableConfig?.COLUMN_DEFINITIONS || [];
    return columnDefinitions;
  }, [reportConfig]);

  // Generate columns based on data structure (use original data for column generation)
  const columns = useMemo(() => {
    // Use original data for column generation (sorting is only for display)
    if (!data || data.length === 0) return [];

    // Get column config from table.configs
    const modelName = reportConfig?.modelName || "";
    const { tableConfig } = getTableConfig(modelName);
    const columnDefinitions = tableConfig?.COLUMN_DEFINITIONS || [];

    // Helper function to format currency
    const formatCurrency = (value) => {
      if (value === null || value === undefined || isNaN(value)) return "$0.00";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    };

    // Helper function to format number
    const formatNumber = (value) => {
      if (value === null || value === undefined || isNaN(value)) return "0";
      return value.toLocaleString();
    };

    // Helper function to get cell value for group vs child rows
    const getCellValue = (cellProps, accessorKey) => {
      const row = cellProps.row.original;
      if (row._isGroupRow) {
        // For group rows, return group totals
        const groupFieldMap = {
          payDate: row._groupPayDate || row.payDate,
          total: row._groupTotalAmount,
          totalAmount: row._groupTotalAmount,
          totalMileage: row._groupTotalMileage,
          hours: row._groupTotalHours,
          mileageTotal: row._groupTotalMileageTotal,
          hourlyTotal: row._groupTotalHourlyTotal,
          supplementalTotal: row._groupTotalSupplementalTotal,
          status: "Grouped",
          driver:
            row.subRows?.length > 0
              ? row.subRows.length === 1
                ? row.subRows[0].driver?.fullName ||
                  row.subRows[0].employee?.fullName ||
                  "N/A"
                : `${row.subRows.length} payouts`
              : "N/A",
          employee:
            row.subRows?.length > 0
              ? row.subRows.length === 1
                ? row.subRows[0].employee?.fullName ||
                  row.subRows[0].driver?.fullName ||
                  "N/A"
                : `${row.subRows.length} payouts`
              : "N/A",
        };
        return groupFieldMap[accessorKey] !== undefined
          ? groupFieldMap[accessorKey]
          : row[accessorKey];
      }
      // For child rows, return individual payout data
      return cellProps.getValue();
    };

    // If we have column definitions from config, use them
    if (columnDefinitions.length > 0) {
      // Add manifest range column for grouped payouts
      let columnsToUse = [...columnDefinitions];
      if (isGrouped && (modelName === "Payout" || modelName === "Payouts")) {
        // Find payDate index
        const payDateIndex = columnsToUse.findIndex(
          (col) => col.id === "payDate"
        );

        // Create manifest range column
        const manifestRangeColumn = {
          id: "manifestRange",
          header: "Manifest Range",
          minWidth: 180,
          accessorKey: "manifestRange",
          cell: ({ row }) => {
            const rowData = row.original;
            if (rowData._isGroupRow) {
              const startDate = rowData._groupManifestStartDate;
              const endDate = rowData._groupManifestEndDate;
              if (startDate && endDate) {
                return (
                  <div className="font-semibold">
                    {formatOperationalWindowStartForDisplay(startDate)} -{" "}
                    {formatOperationalWindowEndForDisplay(endDate)}
                  </div>
                );
              } else if (startDate || endDate) {
                return (
                  <div className="font-semibold">
                    {startDate
                      ? formatOperationalWindowStartForDisplay(startDate)
                      : formatOperationalWindowEndForDisplay(endDate)}
                  </div>
                );
              }
              return <div className="font-semibold text-gray-400">-</div>;
            }
            // For child rows, try to get manifest dates from the payout
            const manifestInfo =
              rowData.manifestInfo || rowData.manifestData?.[0];
            if (manifestInfo?.startDate && manifestInfo?.endDate) {
              return (
                <div>
                  {formatOperationalWindowStartForDisplay(
                    manifestInfo.startDate,
                  )}{" "}
                  - {formatOperationalWindowEndForDisplay(manifestInfo.endDate)}
                </div>
              );
            }
            return "-";
          },
        };

        // Insert manifest range column after payDate
        if (payDateIndex !== -1) {
          columnsToUse.splice(payDateIndex + 1, 0, manifestRangeColumn);
        } else {
          // If payDate not found, insert at the beginning after payoutId
          const payoutIdIndex = columnsToUse.findIndex(
            (col) => col.id === "payoutId"
          );
          if (payoutIdIndex !== -1) {
            columnsToUse.splice(payoutIdIndex + 1, 0, manifestRangeColumn);
          } else {
            // Insert at position 1 (after first column)
            columnsToUse.splice(1, 0, manifestRangeColumn);
          }
        }
      }

      // For grouped payouts, create simplified columns for group rows
      // Order: Pay Date, Manifest Range, # of Payouts, Total
      if (isGrouped && (modelName === "Payout" || modelName === "Payouts")) {
        // Filter to only essential columns for group rows and maintain order
        const essentialColumnIds = [
          "payDate",
          "manifestRange",
          "payoutId",
          "total",
        ];
        const essentialColumns = [];
        essentialColumnIds.forEach((id) => {
          const col = columnsToUse.find((c) => (c.id || c.accessorKey) === id);
          if (col) essentialColumns.push(col);
        });
        columnsToUse = essentialColumns;
      }

      // Map columns - ensure first column cell renderer can be wrapped by renderColumns
      // Create a mutable array so renderColumns can modify columns[0]
      const mappedColumns = columnsToUse.map((col, colIndex) => {
        const accessorKey = col.accessorKey || col.id;
        const isGroupedColumn = isGrouped && accessorKey;
        const originalCell = col.cell;
        const isFirstColumn = colIndex === 0;

        // For the first column, we need to preserve the cell renderer so renderColumns can wrap it
        // renderColumns will add the DropArrow automatically
        if (isFirstColumn && isGrouped) {
          return {
            ...col,
            cell: (cellProps) => {
              const row = cellProps.row.original;
              const isGroupRow = row._isGroupRow;

              // Handle ID columns - show count for group rows, link for child rows
              const isIdColumn =
                accessorKey?.toLowerCase().endsWith("id") || col.id === "_id";

              // For group rows, show payout count instead of ID
              if (isIdColumn && isGroupRow && accessorKey === "payoutId") {
                // Return the content - renderColumns will wrap this with DropArrow
                return (
                  <div className="font-semibold">
                    {row.subRows?.length || 0} payout
                    {row.subRows?.length !== 1 ? "s" : ""}
                  </div>
                );
              }

              // For non-group rows or other cases, use original cell renderer
              return originalCell
                ? originalCell(cellProps)
                : cellProps.getValue();
            },
          };
        }

        return {
          ...col,
          cell: (cellProps) => {
            const row = cellProps.row.original;
            const isGroupRow = row._isGroupRow;

            // Handle ID columns - show count for group rows, link for child rows
            const isIdColumn =
              accessorKey?.toLowerCase().endsWith("id") || col.id === "_id";

            // For group rows, show payout count instead of ID
            if (isIdColumn && isGroupRow && accessorKey === "payoutId") {
              return (
                <div className="font-semibold">
                  {row.subRows?.length || 0} payout
                  {row.subRows?.length !== 1 ? "s" : ""}
                </div>
              );
            }

            if (isIdColumn && !isGroupRow) {
              const routeMap = {
                Payout: "payouts",
                Payouts: "payouts",
                Job: "jobs",
                Jobs: "jobs",
                Invoice: "invoices",
                Invoices: "invoices",
                Manifest: "manifests",
                Manifests: "manifests",
                Leg: "legs",
                Legs: "legs",
              };
              const routePath = routeMap[modelName] || modelName.toLowerCase();
              const value = cellProps.getValue();
              const rowId = row._id;
              if (rowId && !rowId.startsWith("group-")) {
                return (
                  <a
                    href={`/${routePath}/${rowId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/${routePath}/${rowId}`);
                    }}
                    className="text-primary-600 hover:underline cursor-pointer"
                  >
                    {value || rowId}
                  </a>
                );
              }
            }

            // Handle manifestRange column separately (it has its own cell renderer)
            if (accessorKey === "manifestRange") {
              // Use the original cell renderer for manifestRange (already defined in column)
              return originalCell ? originalCell(cellProps) : "-";
            }

            // For group rows, show group totals with styling
            if (isGroupRow && isGroupedColumn) {
              const value = getCellValue(cellProps, accessorKey);
              const isCurrency =
                (/total|amount|pay|charge|price|cost/i.test(accessorKey) &&
                  !/mileage|hours|count|quantity/i.test(accessorKey)) ||
                /mileagePay|payoutMileage|mileageTotal/i.test(accessorKey);
              const isNumeric = typeof value === "number";
              const isDate =
                accessorKey === "payDate" || accessorKey === "dateCreated";

              // Format date for group rows
              if (isDate && value) {
                return <div className="font-semibold">{formatDate(value)}</div>;
              }

              // Format currency amounts for group rows
              if (isCurrency && isNumeric) {
                return (
                  <div className="font-semibold">{renderAmount(value)}</div>
                );
              }

              // Format mileage for group rows (if it's totalMileage - miles, not currency)
              if (accessorKey === "totalMileage" && isNumeric) {
                return (
                  <div className="font-semibold">{formatNumber(value)} mi</div>
                );
              }

              return (
                <div className="font-semibold">
                  {isNumeric ? formatNumber(value) : value}
                </div>
              );
            }

            // For child rows, use custom cell renderer if provided, otherwise default
            return originalCell ? (
              originalCell(cellProps)
            ) : (
              <ReportTableCell {...cellProps} />
            );
          },
        };
      });

      // Return the mapped columns array (renderColumns will modify columns[0] to add DropArrow)
      return mappedColumns;
    }

    // Fallback to auto-generated columns (existing logic)
    const defaultOptions = {
      excludeColumns: ["__v", "_id", "dateCreated", "dateUpdated"], // Hide internal MongoDB fields by default
      columnPriority: ["driver", "total", "status"],
      ...columnOptions,
    };

    const baseColumns = generateColumnsFromData(data, defaultOptions);

    // Enhance columns with custom cell renderer and make ID/primary key columns clickable
    const routeMap = {
      Payout: "payouts",
      Payouts: "payouts",
      Job: "jobs",
      Jobs: "jobs",
      Invoice: "invoices",
      Invoices: "invoices",
      Manifest: "manifests",
      Manifests: "manifests",
      Leg: "legs",
      Legs: "legs",
    };
    const routePath = routeMap[modelName] || modelName.toLowerCase();

    return baseColumns.map((column) => {
      const accessorKey = column.accessorKey || column.id;
      const isGroupedColumn = isGrouped && accessorKey;

      // Make ID columns clickable (payoutId, jobId, invoiceId, etc.) - only for child rows
      const isIdColumn =
        column.id?.toLowerCase().endsWith("id") ||
        accessorKey?.toLowerCase().endsWith("id") ||
        column.id === "_id";

      return {
        ...column,
        cell: (cellProps) => {
          const row = cellProps.row.original;
          const isGroupRow = row._isGroupRow;

          // Handle ID columns for child rows only
          if (isIdColumn && routePath && !isGroupRow) {
            const value = cellProps.getValue();
            const rowId = row._id;
            if (rowId && !rowId.startsWith("group-")) {
              return (
                <a
                  href={`/${routePath}/${rowId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/${routePath}/${rowId}`);
                  }}
                  className="text-primary-600 hover:underline cursor-pointer"
                >
                  {value || rowId}
                </a>
              );
            }
          }

          // For group rows, show group totals with styling
          if (isGroupRow && isGroupedColumn) {
            const value = getCellValue(cellProps, accessorKey);
            const isCurrency =
              (/total|amount|pay|charge|price|cost/i.test(accessorKey) &&
                !/mileage|hours|count|quantity/i.test(accessorKey)) ||
              /mileagePay|payoutMileage|mileageTotal/i.test(accessorKey);
            const isNumeric = typeof value === "number";
            const isDate =
              accessorKey === "payDate" || accessorKey === "dateCreated";

            // Format date for group rows
            if (isDate && value) {
              return <div className="font-semibold">{formatDate(value)}</div>;
            }

            return (
              <div className="font-semibold">
                {isCurrency && isNumeric
                  ? formatCurrency(value)
                  : isNumeric
                  ? formatNumber(value)
                  : value}
              </div>
            );
          }

          // For child rows, use default cell renderer
          return <ReportTableCell {...cellProps} />;
        },
      };
    });
  }, [data, columnOptions, reportConfig, navigate, isGrouped]);

  // Generate summary statistics
  // For grouped reports, use original data (not transformed groupedData) for accurate totals
  // For non-grouped reports, use sortedData
  const summary = useMemo(() => {
    if (!showSummary) return null;

    // Check if data is grouped (has _isGroupRow in sortedData)
    const isGroupedData =
      sortedData && sortedData.some((item) => item._isGroupRow);

    // For grouped reports, use original data (flat array with all payouts) for accurate totals
    // For non-grouped reports, use sortedData
    const dataForSummary = isGroupedData ? data : sortedData;

    if (!dataForSummary || dataForSummary.length === 0) return null;

    // Always use the flat data array for summary calculation
    // For grouped data, this is already the original flat array
    // For non-grouped data, this is sortedData
    return generateReportSummary(dataForSummary, reportName);
  }, [data, sortedData, reportName, showSummary]);

  // Filter data for selected field
  const filteredData = useMemo(() => {
    if (!selectedField || !sortedData || sortedData.length === 0) return [];

    // Handle status breakdown - filter by status value
    if (selectedField.type === "status" && selectedField.status) {
      return sortedData.filter((item) => {
        const statusValue = item[selectedField.cleanKey];
        return statusValue === selectedField.status;
      });
    }

    // Handle array count - show all items with that array field
    if (selectedField.type === "count") {
      return sortedData.filter((item) => {
        const arrayValue = item[selectedField.cleanKey];
        return Array.isArray(arrayValue) && arrayValue.length > 0;
      });
    }

    // Handle nested array summaries - flatten nested items
    if (
      selectedField.type === "nested_total" &&
      selectedField.arrayField &&
      selectedField.nestedField
    ) {
      const allNestedItems = [];
      sortedData.forEach((item) => {
        const arrayField = item[selectedField.arrayField];
        if (Array.isArray(arrayField)) {
          arrayField.forEach((nestedItem, index) => {
            if (nestedItem && typeof nestedItem === "object") {
              allNestedItems.push({
                ...nestedItem,
                _parentId: item._id || item.payoutId || `item-${index}`,
                _parentData: item, // Include parent data for context
              });
            }
          });
        }
      });
      return allNestedItems;
    }

    // Default: Return all data items that have the selected field
    return sortedData.filter((item) => {
      const fieldValue = item[selectedField.cleanKey];
      return fieldValue !== null && fieldValue !== undefined;
    });
  }, [selectedField, sortedData]);

  // Get model-specific key fields to highlight
  const getKeyFields = (modelName) => {
    const commonFields = {
      total: "Total",
      amount: "Amount",
      mileageTotal: "Total Mileage",
      totalMileage: "Mileage",
      hours: "Hours",
      supplementalPay: "Supplemental Pay",
      mileagePay: "Mileage Pay",
    };

    const modelSpecific = {
      Payout: {
        total: "Total Payout",
        mileageTotal: "Mileage Total",
        totalMileage: "Total Mileage",
        hours: "Total Hours",
        supplementalPay: "Supplemental Pay",
        mileagePay: "Mileage Pay",
      },
      Job: {
        total: "Total",
        totalCharges: "Total Charges",
        baseCharge: "Base Charges",
        fuelSurcharge: "Fuel Surcharge",
        otherCharges: "Other Charges",
      },
      Invoice: {
        total: "Total Amount",
        totalBaseCharges: "Base Charges",
        totalDropCharges: "Drop Charges",
        totalFuelCharges: "Fuel Charges",
        totalOtherCharges: "Other Charges",
      },
      Invoices: {
        total: "Total Amount",
        totalBaseCharges: "Base Charges",
        totalDropCharges: "Drop Charges",
        totalFuelCharges: "Fuel Charges",
        totalOtherCharges: "Other Charges",
      },
      Leg: {
        totalCharges: "Total Charges",
        baseCharge: "Base Charge",
        fuelSurcharge: "Fuel Surcharge",
        otherCharges: "Other Charges",
      },
      Manifest: {
        totalMileage: "Total Mileage",
        totalLegs: "Total Legs",
      },
    };

    return { ...commonFields, ...(modelSpecific[modelName] || {}) };
  };

  // Handle CSV export
  const handleExport = () => {
    const filename = `${reportName.toLowerCase().replace(/\\s+/g, "-")}-report`;
    exportToCsv(sortedData, filename);
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    try {
      // Get parentCompany info from props, with fallback
      const parentCompanyForPDF = parentCompany || { name: "Company" };

      // console.log("=== handleDownloadPDF called ===");
      // console.log("data length:", sortedData?.length);
      // console.log("summary:", summary);
      // console.log(
      //   "summary keys:",
      //   summary ? Object.keys(summary) : "no summary"
      // );
      // if (summary) {
      //   console.log("summary.totalSum:", summary.totalSum);
      //   console.log("summary.total:", summary.total);
      //   console.log(
      //     "summary.legs_payoutTotalSum:",
      //     summary.legs_payoutTotalSum
      //   );
      //   console.log("summary.legsCount:", summary.legsCount);
      // }
      // console.log("reportConfig:", reportConfig);
      // console.log("reportMetadata:", reportMetadata);
      // console.log("parentCompany:", parentCompanyForPDF);

      // For PDF, use original data (not transformed groupedData)
      // GroupedPayDateSummaryTable expects flat array with _groupPayDate fields
      const pdfData = isGrouped ? data : sortedData;

      await downloadReportPDF({
        data: pdfData, // Use original data for grouped reports, sortedData for others
        summary,
        reportName,
        reportConfig,
        reportMetadata,
        parentCompany: parentCompanyForPDF,
        hasMultipleEmployees, // Pass flag for multi-employee layout
        hasMultipleClients,
        analytics,
      });
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  // Handle saving report
  const handleSaveReport = async () => {
    try {
      // Helper function to convert plural model name to singular for API
      const getApiModelName = (modelName) => {
        const modelNameMap = {
          Payouts: "Payout",
          Manifests: "Manifest",
          Jobs: "Job",
          Legs: "Leg",
          Invoices: "Invoice",
        };
        return modelNameMap[modelName] || modelName;
      };

      // Convert model name to singular for API
      const apiModelName = getApiModelName(
        reportConfig?.modelName || "Unknown"
      );

      // Save query parameters instead of data for regeneration
      // This allows reports to always show current data when opened
      const saveData = {
        reportName: reportName,
        modelName: apiModelName, // Use singular form
        description: `Saved report query - regenerates with current data`,
        // Save query parameters for regeneration
        queryParameters: reportRequest || {
          // Fallback: construct query from available data if reportRequest not provided
          modelName: apiModelName,
          config: reportConfig?.config || {},
          reportMetadata: reportMetadata,
        },
        // Optionally save a snapshot of current data for reference
        // Set to null to save only query parameters (recommended)
        reportData: null, // Set to null to save only query, or sortedData for snapshot
        // Include metadata for display purposes
        reportMetadata: reportMetadata,
        // Mark as query-based report (regenerates on view)
        isQueryBased: true,
      };

      await saveReport(saveData).unwrap();
      toast.success(
        "Report saved successfully! It will regenerate with current data when opened."
      );
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error(error?.data?.message || "Failed to save report");
    }
  };

  // Render summary card
  const renderSummary = () => {
    if (!summary) return null;

    const modelName = reportConfig?.modelName || "";
    const keyFields = getKeyFields(modelName);

    // Extract all numeric summaries
    const allSummaries = Object.entries(summary)
      .filter(
        ([key, value]) =>
          key.endsWith("Sum") ||
          key.endsWith("Avg") ||
          key.endsWith("Min") ||
          key.endsWith("Max") ||
          key.endsWith("Count")
      )
      .filter(([key, value]) => typeof value === "number");

    // Extract array counts (legsCount, shiftsCount, etc.)
    const arrayCounts = Object.entries(summary)
      .filter(
        ([key, value]) => key.endsWith("Count") && typeof value === "number"
      )
      .map(([key, value]) => {
        const cleanKey = key.replace("Count", "");
        const label =
          keyFields[cleanKey] ||
          cleanKey
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
        return {
          key,
          value,
          cleanKey,
          label,
        };
      })
      .sort((a, b) => b.value - a.value); // Sort by count descending

    // Extract status breakdowns
    const statusBreakdowns = Object.entries(summary)
      .filter(
        ([key, value]) => key.endsWith("Breakdown") && typeof value === "object"
      )
      .map(([key, value]) => {
        const cleanKey = key.replace("Breakdown", "");
        const label =
          keyFields[cleanKey] ||
          cleanKey
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
        return {
          key,
          cleanKey,
          label,
          breakdown: value,
        };
      });

    // Extract nested array summaries (e.g., legs_totalSum)
    const nestedSummaries = Object.entries(summary)
      .filter(
        ([key, value]) =>
          key.includes("_") &&
          (key.endsWith("Sum") || key.endsWith("Avg")) &&
          typeof value === "number"
      )
      .map(([key, value]) => {
        const [arrayField, nestedField] = key.split("_");
        const suffix = key.endsWith("Sum") ? "Sum" : "Avg";
        const cleanNestedField = nestedField.replace(suffix, "");
        const arrayLabel =
          keyFields[arrayField] ||
          arrayField
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
        const nestedLabel =
          keyFields[cleanNestedField] ||
          cleanNestedField
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
        const isCurrency =
          /total|amount|pay|charge|price|cost/i.test(cleanNestedField) &&
          !/mileage|hours|count|quantity/i.test(cleanNestedField);

        return {
          key,
          value,
          arrayField,
          nestedField: cleanNestedField,
          label: `${arrayLabel} - ${nestedLabel}`,
          isCurrency,
          type: suffix === "Sum" ? "total" : "average",
        };
      });

    // Separate sums (totals) from other stats
    const sums = allSummaries.filter(([key]) => key.endsWith("Sum"));
    const otherStats = allSummaries.filter(([key]) => !key.endsWith("Sum"));

    // Prioritize key fields for display
    const prioritizedSums = sums
      .map(([key, value]) => {
        const cleanKey = key.replace("Sum", "");
        const label = keyFields[cleanKey] || cleanKey;
        // Include mileage pay fields as currency (mileagePay, mileageTotal - but NOT totalMileage which is miles)
        const isCurrency =
          (/total|amount|pay|charge|price|cost/i.test(cleanKey) &&
            !/mileage|hours|count|quantity/i.test(cleanKey)) ||
          /mileagePay|payoutMileage/i.test(cleanKey) ||
          cleanKey === "mileageTotal" || // mileageTotal is pay (currency), totalMileage is miles (not currency)
          cleanKey === "total" ||
          cleanKey === "amount";

        return {
          key,
          value,
          cleanKey,
          label,
          isCurrency,
          priority: keyFields[cleanKey] ? 1 : 2, // Prioritize known fields
        };
      })
      .sort((a, b) => {
        // Sort by priority, then by currency (totals first), then alphabetically
        if (a.priority !== b.priority) return a.priority - b.priority;
        if (a.isCurrency !== b.isCurrency) return a.isCurrency ? -1 : 1;
        return a.label.localeCompare(b.label);
      });

    // Get top totals (first 6-8 most important)
    const topTotals = prioritizedSums.slice(0, 8);

    // Get averages for key fields
    const averages = otherStats
      .filter(([key]) => key.endsWith("Avg"))
      .map(([key, value]) => {
        const cleanKey = key.replace("Avg", "");
        const label = keyFields[cleanKey] || cleanKey;
        // Include mileage pay fields as currency (mileagePay, mileageTotal - but NOT totalMileage which is miles)
        const isCurrency =
          (/total|amount|pay|charge|price|cost/i.test(cleanKey) &&
            !/mileage|hours|count|quantity/i.test(cleanKey)) ||
          /mileagePay|payoutMileage/i.test(cleanKey) ||
          cleanKey === "mileageTotal" || // mileageTotal is pay (currency), totalMileage is miles (not currency)
          cleanKey === "total" ||
          cleanKey === "amount";

        return {
          key,
          value,
          cleanKey,
          label,
          isCurrency,
        };
      })
      .filter((item) => keyFields[item.cleanKey] || item.isCurrency)
      .slice(0, 6);

    return (
      <Card className="mb-4">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-3" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {reportName}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {summary.totalRecords} record
                  {summary.totalRecords !== 1 ? "s" : ""} found
                </p>
              </div>
            </div>
            {renderActions()}
          </div>
        </div>

        <div className="px-6 py-6">
          {/* Main Totals Section */}
          {topTotals.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                Totals
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {topTotals.map(
                  ({ key, value, label, isCurrency, cleanKey }) => (
                    <div
                      key={key}
                      onClick={() => {
                        setSelectedField({
                          cleanKey,
                          label,
                          type: "total",
                          value,
                        });
                        setShowDetailModal(true);
                      }}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200 cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-colors"
                    >
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        {label}
                      </div>
                      <div
                        className={`text-2xl font-bold ${
                          isCurrency ? "text-green-600" : "text-gray-900"
                        }`}
                      >
                        {isCurrency
                          ? new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                            }).format(value)
                          : value.toLocaleString()}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Averages Section */}
          {/* {averages.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                Averages
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {averages.map(({ key, value, label, isCurrency, cleanKey }) => (
                  <div
                    key={key}
                    onClick={() => {
                      setSelectedField({
                        cleanKey,
                        label,
                        type: "average",
                        value,
                      });
                      setShowDetailModal(true);
                    }}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200 cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-colors"
                  >
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {label}
                    </div>
                    <div
                      className={`text-lg font-semibold ${
                        isCurrency ? "text-blue-600" : "text-gray-700"
                      }`}
                    >
                      {isCurrency
                        ? new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                          }).format(value)
                        : typeof value === "number"
                        ? value.toFixed(2)
                        : value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {/* Array Counts Section (Legs, Shifts, etc.) */}
          {arrayCounts.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                Item Counts
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {arrayCounts.map(({ key, value, label, cleanKey }) => (
                  <div
                    key={key}
                    onClick={() => {
                      setSelectedField({
                        cleanKey,
                        label,
                        type: "count",
                        value,
                      });
                      setShowDetailModal(true);
                    }}
                    className="bg-purple-50 rounded-lg p-4 border border-purple-200 cursor-pointer hover:bg-purple-100 hover:border-purple-300 transition-colors"
                  >
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {label}
                    </div>
                    <div className="text-2xl font-bold text-purple-600">
                      {value.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nested Array Summaries Section (e.g., Legs Total, Shifts Total) */}
          {nestedSummaries.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                Nested Item Totals
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {nestedSummaries
                  .filter((item) => item.type === "total")
                  .slice(0, 8)
                  .map(
                    ({
                      key,
                      value,
                      label,
                      isCurrency,
                      arrayField,
                      nestedField,
                    }) => (
                      <div
                        key={key}
                        onClick={() => {
                          setSelectedField({
                            cleanKey: `${arrayField}_${nestedField}`,
                            label,
                            type: "nested_total",
                            value,
                            arrayField,
                            nestedField,
                          });
                          setShowDetailModal(true);
                        }}
                        className="bg-indigo-50 rounded-lg p-4 border border-indigo-200 cursor-pointer hover:bg-indigo-100 hover:border-indigo-300 transition-colors"
                      >
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          {label}
                        </div>
                        <div
                          className={`text-xl font-bold ${
                            isCurrency ? "text-indigo-600" : "text-gray-900"
                          }`}
                        >
                          {isCurrency
                            ? new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                              }).format(value)
                            : value.toLocaleString()}
                        </div>
                      </div>
                    )
                  )}
              </div>
            </div>
          )}

          {/* Status Breakdown Section */}
          {statusBreakdowns.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                Status Breakdown
              </h4>
              <div className="space-y-3">
                {statusBreakdowns.map(({ key, label, breakdown }) => (
                  <div
                    key={key}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                  >
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {label}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(breakdown).map(([status, count]) => (
                        <div
                          key={status}
                          onClick={() => {
                            setSelectedField({
                              cleanKey: key.replace("Breakdown", ""),
                              label: `${label} - ${status}`,
                              type: "status",
                              value: count,
                              status,
                            });
                            setShowDetailModal(true);
                          }}
                          className="bg-white rounded-lg px-3 py-2 border border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-700">
                            {status}:
                          </span>
                          <span className="text-sm font-bold text-gray-900 ml-1">
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Report Info Footer */}
        {reportConfig && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-4 flex-wrap gap-y-2">
                <span>
                  <span className="font-medium">Model:</span>{" "}
                  {reportConfig.modelName}
                </span>
                <span className="text-gray-300">|</span>
                {/* Date Range Display */}
                {(reportMetadata?.dateRangeType === "allTime" ||
                  (reportMetadata?.dateRange?.startDate &&
                    reportMetadata?.dateRange?.endDate)) && (
                  <>
                    <span>
                      <span className="font-medium">Date Range:</span>{" "}
                      {reportMetadata?.dateRangeType === "allTime"
                        ? "All Time"
                        : (() => {
                            const startDate = new Date(
                              reportMetadata.dateRange.startDate
                            );
                            const endDate = new Date(
                              reportMetadata.dateRange.endDate
                            );

                            // Format based on date range type
                            if (reportMetadata.dateRangeType === "custom") {
                              return `${format(
                                startDate,
                                "MMM dd, yyyy"
                              )} - ${format(endDate, "MMM dd, yyyy")}`;
                            } else if (
                              reportMetadata.dateRangeType === "monthly"
                            ) {
                              return format(startDate, "MMMM yyyy");
                            } else if (
                              reportMetadata.dateRangeType === "quarterly"
                            ) {
                              const quarter = Math.ceil(
                                (startDate.getMonth() + 1) / 3
                              );
                              return `Q${quarter} ${format(startDate, "yyyy")}`;
                            } else if (
                              reportMetadata.dateRangeType === "yearly"
                            ) {
                              return format(startDate, "yyyy");
                            }
                            // Fallback
                            return `${format(
                              startDate,
                              "MMM dd, yyyy"
                            )} - ${format(endDate, "MMM dd, yyyy")}`;
                          })()}
                    </span>
                    <span className="text-gray-300">|</span>
                  </>
                )}
                <span>
                  <span className="font-medium">Generated:</span>{" "}
                  {new Date().toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}
      </Card>
    );
  };

  // Render table actions
  const renderActions = () => (
    <div className="flex items-center space-x-2">
      {allowExport && sortedData && sortedData.length > 0 && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPDF}
            className="flex items-center"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
            Download PDF
          </Button>
          {/* <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            className="flex items-center"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
            Export CSV
          </Button> */}
        </>
      )}

      {/* {allowSave && sortedData && sortedData.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleSaveReport}
          disabled={isSaving}
          className="flex items-center"
        >
          <BookmarkIcon className="h-4 w-4 mr-1" />
          {isSaving ? "Saving..." : "Save Report"}
        </Button>
      )}

      {onRefresh && (
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowColumnVisibility(!showColumnVisibility)}
        className="flex items-center"
      >
        {showColumnVisibility ? (
          <EyeSlashIcon className="h-4 w-4 mr-1" />
        ) : (
          <EyeIcon className="h-4 w-4 mr-1" />
        )}
        Columns
      </Button> */}
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <Card className="flex items-center justify-center py-12">
        <Spinner size="lg" />
        <span className="ml-2 text-gray-600">Generating report...</span>
      </Card>
    );
  }

  // Empty state
  if (!sortedData || sortedData.length === 0) {
    return (
      <Card className="text-center py-12">
        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-semibold text-gray-900">No data</h3>
        <p className="mt-1 text-sm text-gray-500">
          No data available for the selected report criteria.
        </p>
        {onRefresh && (
          <div className="mt-6">
            <Button onClick={onRefresh} variant="primary" size="sm">
              Try Again
            </Button>
          </div>
        )}
      </Card>
    );
  }

  // Error state for columns
  if (columns.length === 0) {
    return (
      <Card className="text-center py-12">
        <DocumentTextIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-semibold text-red-900">
          Unable to display data
        </h3>
        <p className="mt-1 text-sm text-red-500">
          Could not determine table structure from the data.
        </p>
      </Card>
    );
  }

  // Render detail modal
  const renderDetailModal = () => {
    if (!selectedField) return null;

    const isCurrency =
      selectedField.cleanKey &&
      ((/total|amount|pay|charge|price|cost/i.test(selectedField.cleanKey) &&
        !/mileage|hours|count|quantity/i.test(selectedField.cleanKey)) ||
        /mileagePay|payoutMileage/i.test(selectedField.cleanKey) ||
        selectedField.cleanKey === "mileageTotal" ||
        selectedField.cleanKey === "total" ||
        selectedField.cleanKey === "amount");

    // Generate columns for the detail table
    const detailColumns = useMemo(() => {
      if (!filteredData || filteredData.length === 0) return [];

      // For nested items, prioritize the nested field
      const priorityFields =
        selectedField.type === "nested_total" && selectedField.nestedField
          ? [
              selectedField.nestedField,
              "_parentId",
              "driver",
              "status",
              "dateCreated",
            ]
          : [selectedField.cleanKey, "driver", "status", "dateCreated"];

      return generateColumnsFromData(filteredData, {
        excludeColumns: ["__v", "_id", "_parentData"], // Exclude parentData to avoid clutter
        columnPriority: priorityFields,
      });
    }, [filteredData, selectedField]);

    return (
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedField(null);
        }}
        title={`${selectedField.label} - Individual Items`}
        size="full"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-900">
                  {selectedField.type === "total"
                    ? "Total"
                    : selectedField.type === "average"
                    ? "Average"
                    : selectedField.type === "count"
                    ? "Count"
                    : selectedField.type === "status"
                    ? "Status"
                    : selectedField.type === "nested_total"
                    ? "Nested Total"
                    : "Overview"}
                  : {selectedField.label}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {filteredData.length} record
                  {filteredData.length !== 1 ? "s" : ""} found
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  {isCurrency
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(selectedField.value)
                    : selectedField.type === "average"
                    ? typeof selectedField.value === "number"
                      ? selectedField.value.toFixed(2)
                      : selectedField.value
                    : selectedField.value.toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {filteredData.length > 0 ? (
            <div className="max-h-[60vh] overflow-y-auto">
              <Table
                data={filteredData}
                columns={detailColumns}
                loading={false}
                search={true}
                pageSize={25}
                hideCheckbox={true}
                enableSorting={true}
                className="shadow-sm border border-gray-200 rounded-lg"
                emptyState={{
                  title: "No Items",
                  description: "No items found for this field.",
                }}
              />
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No items found for this field.</p>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  return (
    <div className="space-y-4">
      {/* Report Summary */}
      {/* {renderSummary()} */}

      {/* Detail Modal */}
      {/* {renderDetailModal()} */}

      {/* Report Data Table */}
      {sortedData && sortedData.length > 0 && (
        <Card className="bg-white">
          <Table
            title="Report Data"
            data={sortedData}
            columns={columns}
            loading={loading}
            search={true}
            actions={renderActions()}
            pageSize={sortedData.length}
            hideFooter={true}
            hideCheckbox={true}
            enableSorting={true}
            className="shadow-sm border border-gray-200 rounded-lg"
            emptyState={{
              title: "No Results",
              description: "No data matches your current filters.",
            }}
            getRowId={isGrouped ? (row) => row._id : undefined}
            getRowCanExpand={isGrouped ? () => true : undefined}
            // Don't use getSubRows when using expandedLayout - we handle expansion manually
            // getSubRows={undefined}
            rowClickExpand={isGrouped}
            expandedLayout={
              isGrouped
                ? ({ row, table }) => {
                    // Only show expandedLayout for group rows, not individual payout rows
                    if (!row.original._isGroupRow) return null;

                    const payouts = row.original.subRows || [];
                    if (payouts.length === 0) return null;

                    const columnCount = columns.length;
                    return (
                      <td
                        colSpan={columnCount}
                        className="bg-gray-50 p-4 border-t border-gray-200"
                      >
                        <div className="ml-8 border-l-2 border-gray-300 pl-4">
                          <div className="text-sm font-medium text-gray-700 mb-2">
                            Individual Payouts ({payouts.length})
                          </div>
                          <Table
                            data={payouts}
                            columns={nestedTableColumns}
                            hideHeader={false}
                            hideCheckbox
                            hideFooter={true}
                            enableSorting={true}
                            pageSize={payouts.length}
                            className="border border-gray-300 rounded-lg bg-white"
                          />
                        </div>
                      </td>
                    );
                  }
                : undefined
            }
            rowClasses={
              isGrouped
                ? (row) => {
                    if (row.original._isGroupRow) {
                      return "bg-gray-50 font-semibold hover:bg-gray-100 cursor-pointer";
                    }
                    return "hover:bg-gray-50";
                  }
                : undefined
            }
            {...tableProps}
          />
        </Card>
      )}
    </div>
  );
};

export default ReportTable;
